import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, X, Plus } from "lucide-react";
import { createSchedule } from "@/lib/api";
import { formatPretty } from "@/lib/time";
import { formatLeadTime } from "@/lib/format";
import { parseImportHash, clearImportHash } from "@/lib/share";

export const ImportFlashDialog = ({ onImported }) => {
  const [payload, setPayload] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const p = parseImportHash();
    if (p) setPayload(p);
  }, []);

  if (!payload) return null;

  const handleAdd = async () => {
    try {
      setSaving(true);
      // If the shared time is in the past, roll forward to the next occurrence
      let at = payload.scheduled_at;
      const t = new Date(at).getTime();
      if (isNaN(t)) throw new Error("bad date");
      let targetMs = t;
      if (targetMs <= Date.now() + 10 * 1000) {
        if (payload.repeat === "daily") {
          while (targetMs <= Date.now()) targetMs += 86400 * 1000;
          at = new Date(targetMs).toISOString();
        } else if (payload.repeat === "weekly") {
          while (targetMs <= Date.now()) targetMs += 7 * 86400 * 1000;
          at = new Date(targetMs).toISOString();
        } else {
          // Default: push 2 minutes out
          at = new Date(Date.now() + 2 * 60 * 1000).toISOString();
        }
      }
      await createSchedule({
        message: payload.message,
        scheduled_at: at,
        sound_enabled: payload.sound_enabled,
        display_mode: payload.display_mode,
        reminder_minutes: payload.reminder_minutes,
        repeat: payload.repeat,
      });
      toast.success("FLASH IMPORTED");
      clearImportHash();
      setPayload(null);
      onImported?.();
    } catch {
      toast.error("IMPORT FAILED");
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    clearImportHash();
    setPayload(null);
  };

  return (
    <div
      data-testid="import-dialog"
      className="fixed inset-0 z-[9100] bg-black/60 flex items-center justify-center p-4"
    >
      <div className="bg-white brutal-border brutal-shadow w-full max-w-md">
        <div className="flex items-center justify-between brutal-border border-t-0 border-l-0 border-r-0 p-4 bg-[#FFDD00]">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" strokeWidth={3} />
            <h3 className="font-display uppercase text-lg">Someone shared a flash</h3>
          </div>
          <button
            data-testid="import-dismiss"
            onClick={handleDismiss}
            className="bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="font-display uppercase text-xl md:text-2xl break-words leading-tight">
            {payload.message}
          </div>
          <div className="font-mono text-xs uppercase text-neutral-700">
            {formatPretty(payload.scheduled_at)}
          </div>
          <div className="flex flex-wrap gap-2">
            {payload.repeat !== "none" && (
              <span className="bg-[#FFDD00] brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                {payload.repeat}
              </span>
            )}
            {payload.sound_enabled && (
              <span className="bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                Beep
              </span>
            )}
            <span className="bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
              {payload.display_mode === "scroll" ? "Scroll" : "Fit"}
            </span>
            {payload.reminder_minutes && (
              <span className="bg-[#00CCFF] brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                {formatLeadTime(payload.reminder_minutes)} before
              </span>
            )}
          </div>
          <button
            data-testid="import-add"
            onClick={handleAdd}
            disabled={saving}
            className="w-full bg-[#FF3311] text-white brutal-border brutal-shadow brutal-interactive font-display uppercase text-lg py-4 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Plus className="w-5 h-5" strokeWidth={3} />
            {saving ? "Adding..." : "Add to my flashes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportFlashDialog;
